import { Check, Cloud, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'

import { cn } from '@/lib/utils'

interface DraftSavedIndicatorProps {
  /** 最后保存时间戳（可以是 number 或 Date） */
  lastSavedAt: number | Date | null
  /** 是否正在保存 */
  isSaving?: boolean
  /** 自定义类名 */
  className?: string
  /** 显示模式：inline（行内）或 floating（浮动） */
  variant?: 'inline' | 'floating'
}

/**
 * 草稿已保存指示器
 * Requirements: 12.3
 */
export function DraftSavedIndicator({
  lastSavedAt,
  isSaving = false,
  className,
  variant = 'inline',
}: DraftSavedIndicatorProps) {
  const [showSaved, setShowSaved] = useState(false)
  const [, forceUpdate] = useState(0)

  // Convert Date to timestamp if needed
  const timestamp = lastSavedAt instanceof Date ? lastSavedAt.getTime() : lastSavedAt

  // 当保存完成时显示 "已保存" 动画
  useEffect(() => {
    if (timestamp && !isSaving) {
      setShowSaved(true)
      const timer = setTimeout(() => setShowSaved(false), 2000)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [timestamp, isSaving])

  // Update relative time every minute
  useEffect(() => {
    if (!timestamp) {return}
    const timer = setInterval(() => forceUpdate((n) => n + 1), 60000)
    return () => clearInterval(timer)
  }, [timestamp])

  const formatLastSaved = (ts: number | null) => {
    if (!ts) {return null}

    const now = Date.now()
    const diff = now - ts

    if (diff < 60000) {
      return '刚刚保存'
    } else if (diff < 3600000) {
      return `${Math.floor(diff / 60000)} 分钟前保存`
    } else if (diff < 86400000) {
      return `${Math.floor(diff / 3600000)} 小时前保存`
    } else {
      return `${Math.floor(diff / 86400000)} 天前保存`
    }
  }

  if (!timestamp && !isSaving) {
    return null
  }

  if (variant === 'floating') {
    return (
      <div
        className={cn(
          'fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-full bg-background/95 px-4 py-2 shadow-lg border backdrop-blur-sm transition-all duration-300',
          showSaved ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none',
          className
        )}
      >
        {isSaving ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">保存中...</span>
          </>
        ) : (
          <>
            <Check className="h-4 w-4 text-green-500" />
            <span className="text-sm text-muted-foreground">草稿已保存</span>
          </>
        )}
      </div>
    )
  }

  // inline variant
  return (
    <div
      className={cn(
        'flex items-center gap-1.5 text-xs text-muted-foreground transition-opacity duration-200',
        className
      )}
    >
      {isSaving ? (
        <>
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>保存中...</span>
        </>
      ) : timestamp ? (
        <>
          <Cloud className="h-3 w-3" />
          <span>{formatLastSaved(timestamp)}</span>
        </>
      ) : null}
    </div>
  )
}
